import type {
    ContractSchemaObj,
    ContractStatusResponseObj,
    FailedContractSchemaObj,
} from '../../checkout-be/src/schemas/getContractStatus'
import type {
    InitCheckoutPayload,
    InitCheckoutResponse,
} from '../../checkout-be/src/schemas/initCheckout'
import { CheckoutHandler } from './sdk'

export const createContract = async (
    authToken: string,
    body: string,
): Promise<string> => {
    const response = await fetch(`${CheckoutHandler.createContractSdkUrl}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
        },
        body,
    })

    if (!response.ok && response.status !== 409) {
        const errorBody = await response.json().catch((e) => console.info(e))
        throw new Error(
            `Error ${response.status}: ${errorBody.message || 'Failed to create contract'}`,
        )
    }

    const responseBody = await response.json()
    return responseBody.transactionId
}

export const getContractStatus = async (
    authToken: string,
    transactionId: string,
): Promise<{
    status: string | 'success' | 'failed' | 'partial_success'
    contracts?: ContractSchemaObj[]
    failedContracts?: FailedContractSchemaObj[]
}> => {
    const response = await fetch(
        `${CheckoutHandler.contractStatusUrl}?transactionId=${transactionId}`,
        {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${authToken}`,
            },
        },
    )

    if (!response.ok) {
        const errorBody = await response.json()
        throw new Error(
            `Error ${response.status}: ${errorBody.message || 'Failed to get contract status'}`,
        )
    }
    // If 204, there is no body, so doing a .json() will throw an error
    if (response.status === 204) {
        return { status: 'PENDING' }
    }
    const responseBody = await response.json()
    return responseBody
}

export const pollContractStatus = (
    authToken: string,
    transactionId: string,
    interval: number,
): Promise<
    | ContractStatusResponseObj
    | {
          status: 'success' | 'timeout' | 'error' | 'partial_success'
          contracts: ContractStatusResponseObj['contracts']
      }
> => {
    return new Promise((resolve) => {
        const timeout = 20000
        const startTime = Date.now()

        // Set the overall timeout
        const timeoutId = window.setTimeout(() => {
            cleanup()
            resolve({ status: 'timeout', contracts: [] })
        }, timeout)
        // Create a cleanup function to ensure we clear both timers
        const cleanup = (): void => {
            if (intervalId) window.clearInterval(intervalId)
            if (timeoutId) window.clearTimeout(timeoutId)
        }

        // Start polling
        const intervalId = window.setInterval(async () => {
            try {
                const contractStatusResponse = await getContractStatus(
                    authToken,
                    transactionId,
                )

                if (contractStatusResponse.status === 'success') {
                    cleanup()
                    resolve({
                        status: contractStatusResponse.status as ContractStatusResponseObj['status'],
                        contracts: contractStatusResponse.contracts ?? [],
                    })
                } else if (contractStatusResponse.status === 'error') {
                    // If the API explicitly returns an error status
                    cleanup()
                    resolve({ status: 'error', contracts: [] })
                } else if (
                    contractStatusResponse.status === 'partial_success'
                ) {
                    cleanup()
                    resolve({
                        status: contractStatusResponse.status as ContractStatusResponseObj['status'],
                        contracts: contractStatusResponse.contracts ?? [],
                        failedContracts:
                            contractStatusResponse.failedContracts ?? [],
                    })
                }
                // If none of these conditions are met, continue polling
            } catch (error) {
                // Only resolve on error if we've exceeded the timeout
                // Otherwise, continue polling
                if (Date.now() - startTime >= timeout) {
                    cleanup()
                    resolve({ status: 'error', contracts: [] })
                }
                console.error('Error polling contract status:', error)
            }
        }, interval)
    })
}

export const initCheckout = async (
    authToken: string,
    body: InitCheckoutPayload,
): Promise<InitCheckoutResponse> => {
    console.debug(`Calling ${CheckoutHandler.initCheckoutUrl} with body:
    ${body}, and token: ${authToken.slice(0, 5)}...`)
    const response = await fetch(`${CheckoutHandler.initCheckoutUrl}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(body),
    })
    const responseBody = await response.json()
    console.debug(
        `Received body back from init checkout backend: ${responseBody}`,
    )
    return responseBody
}
